import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "ui/card";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { Badge } from "ui/badge";
import { Switch } from "ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "ui/table";
import {
  Search,
  Edit2,
  Building2,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  FileText,
  Activity,
  Users,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "ui/dialog";
import {
  getPropertyAvailabilities,
  getPropertyTypes,
  updatePropertyAvailabilityStatus,
  PropertyAvailabilityListItemApi,
  PropertyTypeApi,
  getPropertyAvailabilityActivityLogs,
  PropertyAvailabilityActivityLogApi,
} from "services/propertyAvailabilityService";
import { toast } from "sonner";
import { FullScreenLoading } from "components/ui/loader";
import Pagination from "ui/pagination";

const Availability = () => {
  const navigate = useNavigate();
  const [availabilities, setAvailabilities] = useState<
    PropertyAvailabilityListItemApi[]
  >([]);
  const [loading, setLoading] = useState(false);

  const [propertyTypes, setPropertyTypes] = useState<PropertyTypeApi[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [isActivityLogsDialogOpen, setIsActivityLogsDialogOpen] =
    useState(false);

  const [selectedActivityLogs, setSelectedActivityLogs] =
    useState<PropertyAvailabilityListItemApi | null>(null);
  const [activityLogs, setActivityLogs] = useState<
    PropertyAvailabilityActivityLogApi[]
  >([]);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({
    propertyName: "",
    propertyType: "all",
    roomType: "all",
    status: "all",
  });

  const getPropertyTypeColor = (type: string) => {
    switch (type) {
      case "Faiz E Husaini ":
        return "bg-green-100 text-green-700 border-green-300";

      case "Hotel":
        return "bg-blue-100 text-blue-700 border-blue-300";

      case "Apartment":
        return "bg-purple-100 text-purple-700 border-purple-300";

      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  useEffect(() => {
    loadAvailabilities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, pageSize, searchTerm, filters.propertyType]);

  const loadAvailabilities = async () => {
    try {
      setLoading(true);

      const response = await getPropertyAvailabilities({
        q: searchTerm || undefined,
        propertyTypeId:
          filters.propertyType === "all"
            ? undefined
            : propertyTypes.find((p) => p.name === filters.propertyType)?.id,
        pageNo,
        pageSize,
      });

      setAvailabilities(response.items);
      setTotalCount(response.totalCount);
    } catch {
      toast.error("Failed to load property availabilities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadPropertyTypes = async () => {
      try {
        const data = await getPropertyTypes();
        setPropertyTypes(data);
      } catch {
        toast.error("Failed to load property types");
      }
    };

    loadPropertyTypes();
  }, []);

  const filteredAvailabilities = availabilities;

  const sortedAvailabilities = [...filteredAvailabilities].sort((a, b) => {
    if (!sortColumn) return 0;

    const key = sortColumn as keyof PropertyAvailabilityListItemApi;
    let aValue = String(a[key] || "");
    let bValue = String(b[key] || "");

    if (sortColumn === "numberOfRooms") {
      aValue = String(Number(aValue));
      bValue = String(Number(bValue));
    }

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const toggleStatus = async (publicId: string, currentStatus: boolean) => {
    try {
      await updatePropertyAvailabilityStatus(publicId, {
        isActive: !currentStatus,
      });

      setAvailabilities((prev) =>
        prev.map((item) =>
          item.publicId === publicId
            ? {
                ...item,
                isActive: !currentStatus,
              }
            : item,
        ),
      );

      toast.success(
        `Availability ${
          !currentStatus ? "activated" : "deactivated"
        } successfully`,
      );
    } catch (error) {
      console.error("Failed to update status:", error);

      toast.error("Failed to update status");
    }
  };

  const handleViewActivityLogs = async (
    availability: PropertyAvailabilityListItemApi,
  ) => {
    try {
      setLoading(true);

      const response = await getPropertyAvailabilityActivityLogs(
        availability.publicId,
      );

      setSelectedActivityLogs(availability);

      setActivityLogs(response.items);

      setIsActivityLogsDialogOpen(true);
    } catch {
      toast.error("Failed to load activity logs");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
        {/* Search + Property Type Filter */}

        <div
          className="flex flex-col lg:flex-row gap-3"
          style={{ marginTop: "0px" }}
        >
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />

            <Input
              placeholder="Search by property name"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPageNo(1);
              }}
              className="pl-10"
            />
          </div>

          {/* Property Type Filter */}
          <div className="w-full lg:w-[220px]">
            <Select
              value={filters.propertyType}
              onValueChange={(value) => {
                setFilters({
                  ...filters,
                  propertyType: value,
                });

                setPageNo(1);
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="all">All Property Types</SelectItem>

                {propertyTypes.map((type) => (
                  <SelectItem key={type.id} value={type.name}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Availability Table */}
        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{ gap: "0px", marginTop: "10px" }}
        >
          <CardContent style={{ padding: "0px 0px 10px 0px" }}>
            {/* Desktop Table View */}
            <div className="hidden lg:block w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("propertyName")}
                    >
                      <div className="flex items-center gap-1">
                        Property Name
                        {sortColumn === "propertyName" ? (
                          sortDirection === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Property Type
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Tour Type
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Date Ranges
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Room Configurations
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Status
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs text-right"
                    >
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAvailabilities.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className="text-center py-8 px-4 text-gray-500 text-sm"
                      >
                        No property availability found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedAvailabilities.map((availability) => (
                      <TableRow
                        key={availability.publicId}
                        className="hover:bg-sky-50/30"
                      >
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-4 h-4 text-[#5B9BD5]" />
                            </div>
                            <span className="text-gray-900 text-sm font-medium">
                              {availability.propertyName}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <Badge
                            className={`${getPropertyTypeColor(availability.propertyTypeName)} text-[10px] px-1.5 py-0.5`}
                          >
                            {availability.propertyTypeName}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {availability?.availableTourOperatorTypeNames?.map(
                              (range, i) => (
                                <Badge
                                  key={range.toString()}
                                  variant="outline"
                                  className="text-[10px] border-green-300 text-gray-700 px-1.5 py-0.5"
                                >
                                  {range}
                                </Badge>
                              ),
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {availability.dateRanges.map((range) => (
                              <Badge
                                key={range.dateRangeIndex}
                                variant="outline"
                                className="text-[10px] border-gray-300 text-gray-700 px-1.5 py-0.5"
                              >
                                {new Date(range.startDate).toLocaleDateString()}{" "}
                                - {new Date(range.endDate).toLocaleDateString()}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="space-y-1">
                            {availability.roomConfigs.map((rc) => (
                              <div
                                key={`${rc.propertyRoomTypeId}-${rc.roomTypeId}`}
                                className="text-xs text-gray-700 flex items-center gap-2"
                              >
                                <span className="font-medium">
                                  {rc.roomTypeName}:
                                </span>
                                <span
                                  className="font-semibold"
                                  style={{ color: "#5B9BD5" }}
                                >
                                  {rc.numberOfRooms}
                                </span>
                                <span className="text-gray-500">
                                  ({rc.availableRooms} available)
                                </span>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <Badge
                            className={`${availability.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                          >
                            {availability.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                navigate(
                                  `/property-availability/edit/${availability.publicId}`,
                                )
                              }
                              className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                              title="Edit"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            <div className="flex items-center justify-center px-2">
                              <Switch
                                checked={availability.isActive}
                                onCheckedChange={() =>
                                  toggleStatus(
                                    availability.publicId,
                                    availability.isActive,
                                  )
                                }
                                className="data-[state=checked]:bg-[#5B9BD5]"
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                handleViewActivityLogs(availability)
                              }
                              className="h-7 w-7 p-0 hover:bg-blue-50"
                              style={{ color: "#9333ea" }}
                              title="View Activity Logs"
                            >
                              <Activity className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="block lg:hidden p-4 space-y-4">
              {sortedAvailabilities.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No property availability found
                </div>
              ) : (
                sortedAvailabilities.map((availability) => (
                  <Card
                    key={availability.publicId}
                    className="border-[#5B9BD5]/20"
                  >
                    <CardContent className="p-3 sm:p-4 space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3
                            className="font-semibold"
                            style={{ color: "#5B9BD5" }}
                          >
                            {availability.propertyName}
                          </h3>
                          <Badge
                            className={`${getPropertyTypeColor(availability.propertyTypeName)} mt-1 text-xs`}
                          >
                            {availability.propertyTypeName}
                          </Badge>
                        </div>
                        <Badge
                          className={`${availability.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}
                        >
                          {availability.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </div>

                      <div>
                        <span className="text-sm text-gray-500">
                          Date Ranges:
                        </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {availability.dateRanges.map((range) => (
                            <Badge
                              key={range.dateRangeIndex}
                              variant="outline"
                              className="text-xs border-gray-300 text-gray-700 break-words whitespace-normal"
                            >
                              {range.dateLabel}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <div>
                        <span className="text-sm text-gray-500">
                          Room Configurations:
                        </span>
                        <div className="space-y-1 mt-1">
                          {availability.roomConfigs.map((rc) => (
                            <div
                              key={`${rc.propertyRoomTypeId}-${rc.roomTypeId}`}
                              className="text-xs text-gray-700 flex items-center gap-2 bg-gray-50 p-2 rounded"
                            >
                              <span className="font-medium">
                                {rc.roomTypeName}:
                              </span>
                              <span
                                className="font-semibold"
                                style={{ color: "#5B9BD5" }}
                              >
                                {rc.numberOfRooms}
                              </span>
                              <span className="text-gray-500">
                                ({rc.availableRooms} available)
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-3 pt-3 w-full">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            navigate(
                              `/property-availability/edit/${availability.publicId}`,
                            )
                          }
                          className="text-[#5B9BD5] hover:bg-sky-50 h-9 w-9 p-0 shrink-0"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>

                        <Switch
                          checked={availability.isActive}
                          onCheckedChange={() =>
                            toggleStatus(
                              availability.publicId,
                              availability.isActive,
                            )
                          }
                          className="data-[state=checked]:bg-[#5B9BD5]"
                        />

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewActivityLogs(availability)}
                          className="text-purple-600 hover:bg-purple-100 h-9 w-9 p-0 shrink-0"
                        >
                          <Activity className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>
          {totalCount > pageSize && (
            <Pagination
              totalItems={totalCount}
              itemsPerPage={pageSize}
              currentPage={pageNo}
              onPageChange={(page) => setPageNo(page)}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPageNo(1);
              }}
              label="availabilities"
            />
          )}
        </Card>

        {/* Activity Logs Dialog */}
        <Dialog
          open={isActivityLogsDialogOpen}
          onOpenChange={setIsActivityLogsDialogOpen}
        >
          <DialogContent
            className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
            style={{ gap: "0px" }}
          >
            <DialogHeader>
              <DialogTitle
                style={{ color: "#5B9BD5" }}
                className="flex items-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Activity Logs
              </DialogTitle>

              <DialogDescription>
                View all changes made to this property availability
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              {selectedActivityLogs && (
                <>
                  <div className="mb-4 p-3 rounded-lg bg-sky-50 border border-sky-200">
                    <h3
                      className="font-semibold text-sm mb-1"
                      style={{ color: "#5B9BD5" }}
                    >
                      {selectedActivityLogs.propertyName}
                    </h3>
                    {activityLogs.length > 0 && (
                      <div className="text-xs text-gray-600">
                        Created On:{" "}
                        {new Date(
                          activityLogs[0].performedAt,
                        ).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {activityLogs.length > 0 ? (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold text-gray-700">
                        Activity History
                      </h4>

                      {activityLogs.map((log) => (
                        <div
                          key={log.logId}
                          className="flex flex-col sm:flex-row gap-3 p-3 rounded-lg border bg-white hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                              <Users className="w-4 h-4 text-purple-600" />
                            </div>
                          </div>

                          <div className="flex-1">
                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">
                                  {log.performedBy}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {log.remarks}
                                </p>
                              </div>

                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                {new Date(log.performedAt).toLocaleString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "numeric",
                                    day: "numeric",
                                    hour: "numeric",
                                    minute: "2-digit",
                                    hour12: true,
                                  },
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />

                      <p className="text-sm">No activity logs available</p>
                    </div>
                  )}
                </>
              )}
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={() => setIsActivityLogsDialogOpen(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default Availability;
